import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Clock, BookOpen, Wrench, ArrowRight, Video, PlayCircle } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { getCourseColorOption } from '../lib/courseColors';

export const CourseCard = ({ course, featured = false }) => {
  const { t, language } = useLanguage();
  const colorOpt = getCourseColorOption(course.color);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ja-JP').format(price);
  };

  const isOnDemand = course.courseType === 'on-demand' || course.courseType === 'hybrid';

  return (
    <div 
      className={`bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md hover:border-[#00A651] transition-all duration-200 group ${
        featured ? 'lg:col-span-2 lg:row-span-2' : ''
      } ${colorOpt ? `border-t-4 ${colorOpt.border}` : ''}`}
      data-testid={`course-card-${course.id}`}
    >
      {/* Image */}
      <div className={`relative overflow-hidden ${featured ? 'h-48 lg:h-64' : 'h-40'}`}>
        <img 
          src={course.image} 
          alt={course.name[language] || course.name.ja}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          <Badge 
            className={`${
              course.type === 'skill' 
                ? 'bg-[#00A651] text-white' 
                : 'bg-[#FACC15] text-gray-900'
            } font-semibold`}
          >
            {course.type === 'skill' ? t('courses.skillTraining') : t('courses.specialEducation')}
          </Badge>
          {isOnDemand && (
            <Badge className="bg-orange-500 text-white font-semibold flex items-center gap-1">
              <Video className="h-3 w-3" />
              {course.courseType === 'hybrid' 
                ? t('courses.hybrid')
                : t('courses.onDemand')}
            </Badge>
          )}
        </div>
        {featured && (
          <div className="absolute top-3 right-3">
            <Badge className="bg-red-500 text-white font-semibold">
              Featured
            </Badge>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 lg:p-5">
        <h3 className={`font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#00A651] transition-colors flex items-start gap-2 ${
          featured ? 'text-lg lg:text-xl' : 'text-base'
        }`}>
          {colorOpt && <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${colorOpt.bg}`} />}
          <span className="line-clamp-2">{course.name[language] || course.name.ja}</span>
        </h3>

        {featured && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {course.description[language] || course.description.ja}
          </p>
        )}

        {/* Stats */}
        <div className="flex flex-wrap gap-3 mb-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-[#00A651]" />
            <span>{course.days} {t('courses.days')}</span>
          </div>
          <div className="flex items-center gap-1">
            <BookOpen className="h-4 w-4 text-[#00A651]" />
            <span>{course.theoryHours}h {t('courses.theory')}</span>
          </div>
          <div className="flex items-center gap-1">
            <Wrench className="h-4 w-4 text-[#00A651]" />
            <span>{course.practicalHours}h {t('courses.practical')}</span>
          </div>
        </div>

        {/* Price & Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div>
            <span className="text-xs text-gray-500">{t('courses.price')}</span>
            <p className="font-bold text-[#00A651] text-lg font-[Barlow_Condensed]">
              ¥{formatPrice(course.price)}
            </p>
          </div>
          <div className="flex gap-2">
            <Link to={`/courses/${course.id}`}>
              <Button 
                variant="outline" 
                size="sm"
                className="border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-white"
                data-testid={`view-details-${course.id}`}
              >
                {t('courses.viewDetails')}
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
